/**
 * Sky, sun, atmosphere, aerial perspective and valley mist.
 *
 * OWNER: piece:atmosphere
 *
 * Three things live here, and they all share one analytic scattering model so they
 * cannot drift apart:
 *
 *  1. The sky dome. A single-scattering Rayleigh + Mie integral evaluated per pixel,
 *     with a cheap cloud deck projected onto a flat layer above the camera.
 *  2. The lighting. The same model is evaluated on the CPU over a sphere of directions
 *     and projected into an SH9 light probe, so ambient colour genuinely comes from the
 *     sky: blue fill under a clear noon, warm side-light at golden hour, flat grey when
 *     it is overcast. The sun's colour is the atmospheric transmittance along its own
 *     path, which is what makes low sun go orange without a hand-authored ramp.
 *  3. Aerial perspective. `three`'s fog chunks are replaced globally with a height-aware
 *     two-layer scattering integral (a deep haze layer plus a shallow valley mist layer)
 *     so every fogged material in the game gets distance *and altitude* dependent
 *     extinction and in-scatter for free, without any other module knowing about it.
 *     Distant ridges wash out and shift blue; valleys hold mist that burns off with
 *     height. That is most of what sells the scale of a mountain.
 *
 *  4. Terrain self-shadowing at ridge scale (see SunShadowField below). A shadow map
 *     cannot do this: the clipmap does not cast (nine rings of ~130k triangles into a
 *     depth pass per frame is not affordable) and a cascade wide enough to hold a 2 km
 *     ridge would have metre-scale texels. Instead the *shadow line altitude* is swept
 *     analytically over the heightfield on the CPU and sampled per pixel, so a low sun
 *     rakes across the massif and the shadow side is lit only by the sky probe.
 *
 * Everything the weather system wants to move lives in `skyParams`.
 */

import * as THREE from 'three';
import { Tuning } from '../core/tuning';
import { clamp, clamp01, lerp, smoothstep } from '../core/math';
import type { Terrain } from '../world/terrain';

// ---------------------------------------------------------------------------
// Constants. Local to this module by repo convention (tuning.ts is physics-owned).
// ---------------------------------------------------------------------------

export const SkyTuning = {
  /**
   * Absolute radiance scale. Calibrated so that under ACES at exposure 1.0 the zenith
   * lands around 0.4 sRGB and the horizon around 0.85 — bright, but with headroom left
   * so the tone mapper never clips the sky to white.
   */
  sunRadiance: 23,
  /** Rayleigh optical depth per unit air mass at sea level, RGB. Real values. */
  betaR: [0.049, 0.0955, 0.211],
  /** Mie optical depth per unit air mass, per unit turbidity. */
  betaM: 0.0085,
  mieG: 0.76,
  /**
   * Ozone absorption, vertical optical depth, RGB.
   *
   * Not decoration. A pure Rayleigh+Mie single-scattering sky goes **green** at a low
   * sun, and measurably so: blue saturates its ( 1 - e^-tau ) term but loses most of its
   * sunlight to its own long path, red keeps its sunlight but barely scatters, and green
   * wins both ways. Measured here at 17:45 the horizon came out ( 0.59, 0.66, 0.50 ) —
   * G above R above B — which is why distant ridges read pea-green at sunset.
   *
   * Real air does not do this because ozone's Chappuis band absorbs right through the
   * yellow-green (500-700 nm) and hardly touches the blue. That single term is what
   * keeps a twilight sky blue instead of olive. It is applied to the sun's own path, so
   * it costs three multiply-adds and colours the sun, the dome and the aerial
   * perspective consistently — they all read the same transmittance.
   */
  betaO3: [0.0155, 0.045, 0.002],
  /** Ozone sits at ~25 km, so its path grows far more slowly than the near-surface
   *  gases'. Capping the air mass it sees stands in for that. */
  ozoneAirMassCap: 14,
  /** Ceiling on the Mie phase function. See the note in atmosphere(). */
  miePhaseCap: 0.3,
  /**
   * Highlight shoulder on the dome: knee start, and headroom above it. Bounds the
   * forward-scattering lobe (which runs to ~17x white next to a low sun) so the tone
   * mapper still has something to roll off instead of a flat clipped field.
   */
  skyKnee: 2.0,
  skyHeadroom: 4.0,

  /** Radiance of the solar disc itself, before atmospheric transmittance. */
  discRadiance: 140,

  /** Cloud deck altitude, m above sea level, and the horizontal noise scale. */
  cloudAltitude: 2750,
  cloudScale: 0.00088,

  /**
   * Aerial perspective, blue channel extinction per metre at sea level. Real air is
   * far clearer than this; a 3 km world has to exaggerate or nothing reads as distant.
   */
  apBlue: 0.00072,
  /** Scale height of the haze layer, m. */
  apScaleHeight: 1450,
  /** Extra grey extinction per metre in the haze (aerosol, not wavelength selective). */
  apGrey: 0.00009,

  /**
   * Beyond this range extinction ramps achromatically. The terrain clipmap stops at
   * ~1.5 km; without this the last ring ends in a visible edge instead of dissolving.
   */
  farFadeStart: 1150,
  farFadeRate: 0.0017,

  /**
   * Valley mist: the altitude where the layer is at full density, and how fast it thins
   * going up. Re-based in r3 — the old 235 m put the layer four scale heights below the
   * ground the rider is actually on (measured 665 m at the spawn), so it evaluated to
   * 0.6% density and the "valley mist" was doing nothing anywhere in the game. 430 m
   * sits it in the valley floors of a 940 m massif: full strength down in the trees,
   * a tenth of that at the treeline, gone on the shoulder.
   */
  mistRefY: 430,
  mistScaleHeight: 115,
  mistBlue: 0.00085,

  /** Weight of the Mie forward lobe in the aerial-perspective in-scatter. */
  apMieWeight: 0.05,

  /**
   * Artistic scale on sky-derived ambient irradiance. Raised from 0.42 in r3: the whole
   * point of an object shadow is that you can still read the dirt inside it, and at 0.42
   * the shaded side of the mountain crushed to a value where hardpack, loam and rock all
   * looked the same. The shadow-to-light ratio is still about 1:3, which is what a clear
   * sky actually gives you.
   */
  ambientScale: 0.5,
  /** Albedo used for the ground-bounce half of the light probe. */
  groundAlbedo: [0.20, 0.175, 0.14],

  /**
   * Shadow volume half-extent, m. Local because tuning.ts is not ours to edit. This
   * cascade is for *objects* — trees, rocks, the bike — because that is all that casts
   * into it; the mountain shadows itself through SunShadowField instead.
   *
   * 200 m was too generous and it is why r2 had no readable contact shadow: at 2048 the
   * texel was 0.195 m, and a 0.06 m tyre, a 0.09 m fork leg and a 0.4 m rider torso all
   * fall through a filter kernel that size. 96 m gives a 0.094 m texel — the tyre is now
   * most of a texel wide and the bike casts a bike-shaped shadow instead of a smudge.
   * A 25 m fir at this sun still throws 79 m of shadow, which fits inside the box when
   * the tree is anywhere in the near half of it; firs further out than that are handled
   * by the analytic field, which has unlimited range.
   */
  shadowRadius: 96,
  /**
   * How far ahead of the rider the cascade is centred, as a fraction of its radius. The
   * camera trails the bike and looks forward, so a box centred on the bike spends a
   * third of itself behind the near clip plane. Pushing it downhill puts the texels
   * where the player is actually looking.
   */
  shadowLead: 0.42,

  // --- terrain self-shadowing ----------------------------------------------
  /**
   * Penumbra of the analytic terrain shadow, expressed in metres of *altitude* below
   * the shadow line and scaled by the sun's slope so the softness stays roughly
   * constant in ground metres as the sun rises. Clamped so a near-overhead sun does
   * not smear the terminator across a whole hillside.
   */
  terrainShadowSoft: 11,
  terrainShadowSoftMin: 0.8,
  terrainShadowSoftMax: 4.0,
  /**
   * The near tier needs a far tighter one. Softness is in metres of altitude, so it
   * turns into ground metres by dividing by the sun's slope: 7 m of altitude at a
   * 17-degree sun is 22 m of ground, which erases the shadow of a 3 m bank entirely.
   * 1.4 m gives ~4 m of ground penumbra — a real edge you can ride into.
   */
  terrainShadowSoftFine: 4.4,
  terrainShadowSoftFineMin: 0.35,
  terrainShadowSoftFineMax: 1.6,
  /** How much of the direct sun a fully shadowed slope loses. 1 = all of it. */
  terrainShadowStrength: 1.0,
  /** How hard sky-visibility (valley vs ridge openness) modulates ambient. */
  skyVisStrength: 0.62,

  // --- rider contact shadow -------------------------------------------------
  /** How much direct sun the rider's own occlusion removes at its centre. */
  riderShadowStrength: 0.92,
  /** Semi-axes of the rider+bike occluder, m: fore-aft and across. */
  riderShadowLength: 1.02,
  riderShadowWidth: 0.44,
  /**
   * Vertical offset from the reported rider position to the occluder centre, m.
   * BikeState.y is already the centre of mass — about a metre up — so the ellipsoid
   * only needs a nudge down to sit between the wheels and the rider's shoulders.
   */
  riderShadowLift: -0.12,
} as const;

/**
 * Sun shadow depth slab, metres. The light sits SHADOW_DIST up-sun of the snapped
 * cascade centre; the depth range runs from SHADOW_LIFT above that centre (tree crowns
 * and any ground that rises inside the box) to SHADOW_DROP below it (ground that falls
 * away). Keeping the slab to ~320 m instead of the whole 1.2 km light frustum is what
 * lets the constant bias drop to a few centimetres, which is what a contact shadow needs.
 */
const SHADOW_DIST = 420;
const SHADOW_LIFT = 120;
const SHADOW_DROP = 200;

/** Mutable per-frame sky state. The weather system owns these values. */
export interface SkyParams {
  /** Hours, 0..24. */
  timeOfDay: number;
  /** Atmospheric turbidity — haze/aerosol load. 1.8 crisp alpine, 6 murky. */
  turbidity: number;
  /** 0 = clear, 1 = solid deck. */
  cloudCover: number;
  /** Multiplier on cloud lit/shade colours. Storm decks are dark. */
  cloudBrightness: number;
  /** Direct sun multiplier — a thick deck kills the sun without killing the sky. */
  sunDim: number;
  /** Multiplier on the valley mist layer. */
  mistAmount: number;
  /** Multiplier on the whole aerial-perspective extinction. */
  hazeAmount: number;
  /** Cloud drift, m/s. */
  windX: number;
  windZ: number;
  /** Extra ambient, used to keep an overcast scene from going flat-dark. */
  ambientBoost: number;
}

export const skyParams: SkyParams = {
  // Mid-morning, 31 degrees up. This number is a straight fight between two things and
  // it is worth writing down which side won and why.
  //
  // A low sun is what makes the *landform* read: at 17.5 degrees 17% of the heightfield
  // is in its own shadow and every ridge draws itself across the valley below it; at
  // noon that number is 0.0% and the mountain goes flat.
  //
  // But a low sun is also what destroyed the *near field* in r2. A 25 m fir at 17.5
  // degrees throws a 79 m shadow. A stand of them throws one merged 79 m shadow, and
  // measured here that put the whole visible foreground inside it — no dappling, no
  // trail in the light, no contrast left for the bike's own shadow to read against. The
  // frame was mush and the shadow of the thing the player is actually looking at was
  // invisible inside a much larger one.
  //
  // 31 degrees costs about two thirds of the terrain self-shadowing (17% -> 5.4%) and
  // buys back the entire foreground: firs throw 42 m, so a stand breaks into individual
  // shadows with lit ground between them, and a bike on that ground has somewhere to
  // cast. The ridges still rake — just less of the mountain is inside one at once.
  timeOfDay: 7.9,
  turbidity: 2.0,
  cloudCover: 0.32,
  cloudBrightness: 1.0,
  sunDim: 1.0,
  mistAmount: 1.0,
  hazeAmount: 1.0,
  windX: 5.5,
  windZ: 2.0,
  ambientBoost: 0.0,
};

/** Set by the Sky constructor so systems (weather) can reach the live instance. */
export const activeSky: { current: Sky | null } = { current: null };

/**
 * Authoring overrides. Multipliers, not values, so the model stays the source of truth
 * and a capture can A/B a single term without a second build.
 */
export const skyDebug = { terrainShadow: 1, skyVis: 1, aerial: 1, rider: 1, showShadowTerm: 0 };

// ---------------------------------------------------------------------------
// Global aerial-perspective injection.
//
// three's four fog chunks are replaced with a height-aware scattering model. This is
// deliberately done through ShaderChunk rather than per material: terrain, vegetation,
// rocks and the bike are all owned by other modules, and all of them need to sit in the
// same air. The parameter block is a single Float32Array shared by reference across
// every cloned uniform set (cloneUniforms copies typed arrays by reference), so one
// write per frame updates every material in the scene.
//
// Layout, 12 x vec4:
//   0: betaHaze.rgb (per m)      | enable flag
//   1: betaMist.rgb (per m)      | global density multiplier
//   2: sunDir.xyz (world)        | Mie g
//   3: horizon sky radiance.rgb  | 1 / haze scale height
//   4: zenith sky radiance.rgb   | mist reference altitude, m
//   5: sun-facing tint.rgb       | 1 / mist scale height
//   6: Mie in-scatter weight, far-fade start, far-fade rate, spare
//   7: 1 / coarse shadow extent, half texel, shadow penumbra (m), shadow strength
//   8: sky-visibility strength, shadow enable, spare, spare
//   9: below-horizon dome radiance.rgb | spare
//  10: 1 / fine shadow extent, half texel, rider x, rider z
//  11: fine blend start (m), fine blend end (m), spare, fine enable
//  12: rider shadow centre.xyz          | rider shadow strength (0 = off)
//  13: cos(yaw), sin(yaw), half length, half width
//
// If a material somehow lacks the uniform (a hand-written ShaderMaterial that merged
// UniformsLib.fog but not this) every component reads 0, the enable flag is false, and
// the chunk falls back to stock three fog. It degrades, it does not break.
//
// The world-position varying and the terrain sun-occlusion sampler are declared in
// <common> rather than in the fog chunks. <common> is the one chunk every built-in
// shader includes in both stages, so the lighting patch further down can rely on them
// existing whatever a material decided to do about fog.
// ---------------------------------------------------------------------------

const AERIAL_VEC4S = 14;
const aerial = new Float32Array(AERIAL_VEC4S * 4);

// --- terrain sun-occlusion textures ------------------------------------------------
// RG half-float, addressed toroidally by world position with period n*cell.
//   R = altitude of the sun's shadow line above this ground column, metres
//   G = sky visibility of this ground column, 0..1
//
// Two tiers. The coarse one spans 5.1 km at 10 m and carries ridge shadows and sky
// visibility; the fine one spans 512 m at 2 m and carries what you can see from the
// saddle — the shadow of the bank you are riding under, of a roller, of a lip. Neither
// alone is enough: one cannot resolve a 6 m bank, the other cannot see a 2 km ridge.
//
// Allocated once and never replaced: cloneUniforms() copies the reference into every
// material's uniform set, so swapping the object would orphan all of them.

/** Shadow-line altitude meaning "nothing occludes this column". */
const SHADOW_LIT = -2000;

const COARSE_N = 512;
// 5120 m. The wrap seam sits 2560 m from the rider; past ~2.5 km the aerial perspective
// has already eaten 95% of the surface colour, so a wrapped shadow there cannot be seen.
const COARSE_CELL = 10;
const FINE_N = 256;
const FINE_CELL = 2;
/** Where the fine tier hands back to the coarse one, metres from the rider. */
const FINE_BLEND_START = 190;
const FINE_BLEND_END = 240;

function makeOccTexture(n: number): { data: Uint16Array; texture: THREE.DataTexture } {
  const data = new Uint16Array(n * n * 2);
  const lit = THREE.DataUtils.toHalfFloat(SHADOW_LIT);
  const one = THREE.DataUtils.toHalfFloat(1);
  for (let i = 0; i < n * n; i++) {
    data[i * 2] = lit;
    data[i * 2 + 1] = one;
  }
  const texture = new THREE.DataTexture(data, n, n, THREE.RGFormat, THREE.HalfFloatType);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return { data, texture };
}

const coarseOcc = makeOccTexture(COARSE_N);
const fineOcc = makeOccTexture(FINE_N);

function installAerialPerspective(): void {
  const C = THREE.ShaderChunk as unknown as Record<string, string>;

  // ShaderChunk is global and these patches append rather than replace, so a second
  // pass (module re-evaluated under HMR) would redeclare every uniform and varying and
  // fail to compile. Once is once.
  if (C.common.indexOf('gripTerrainLight') >= 0) return;

  C.common = /* glsl */ `${C.common}

varying vec3 vGripWP;
uniform vec4 uGripAerial[ ${AERIAL_VEC4S} ];
uniform sampler2D uGripSunOcc;
uniform sampler2D uGripSunOccFine;

// Terrain sun occlusion (.x) and sky visibility (.y) at a world position.
//
// The textures hold the *altitude of the shadow line*, not a shadow mask, and the
// comparison uses this pixel's own exact world height. That is what lets a 10 m field
// produce an edge that follows real geometry: a lip or a boulder standing proud of the
// shadow line catches light, and a fir whose crown is above it is lit while its trunk
// is not. Interpolating a mask instead would blur the edge over a whole texel of ground.
//
// The fine tier only sees 512 m around the rider, so near its edge it under-reports —
// it cannot know about the ridge two kilometres away. Taking the higher of the two
// shadow lines fixes that: whichever tier found an occluder wins.
vec2 gripTerrainLight( vec3 wp ) {
	if ( uGripAerial[ 8 ].y < 0.5 ) return vec2( 1.0 );
	vec2 guv = wp.xz * uGripAerial[ 7 ].x + uGripAerial[ 7 ].y;
	vec2 gt = texture2D( uGripSunOcc, guv ).rg;
	float gLine = gt.r;
	float gSoft = uGripAerial[ 7 ].z;

	if ( uGripAerial[ 11 ].w > 0.5 ) {
		float gw = 1.0 - smoothstep( uGripAerial[ 11 ].x, uGripAerial[ 11 ].y,
			length( wp.xz - uGripAerial[ 10 ].zw ) );
		if ( gw > 0.002 ) {
			vec2 fuv = wp.xz * uGripAerial[ 10 ].x + uGripAerial[ 10 ].y;
			float gFine = texture2D( uGripSunOccFine, fuv ).r;
			gLine = mix( gLine, max( gLine, gFine ), gw );
			// The penumbra tightens with the tier: a ridge shadow two kilometres out is
			// genuinely soft, the edge of the bank beside you is not.
			gSoft = mix( gSoft, uGripAerial[ 11 ].z, gw );
		}
	}

	float gLit = smoothstep( gSoft, 0.0, gLine - wp.y );
	gLit = 1.0 - uGripAerial[ 7 ].w * ( 1.0 - gLit );

	// --- rider contact shadow -------------------------------------------------------
	//
	// The cascade cannot resolve the thing the player is actually looking at. A 2048 map
	// over 96 m is a 0.094 m texel; a tyre is 0.06 m, a fork leg 0.09 m and a crank arm
	// 0.02 m, so the bike rasterises into the depth map as a scatter of half-covered
	// texels and PCF hands back a 20% grey smudge instead of a shadow. That is exactly
	// what r2 looked like: a bike parked on the dirt with nothing underneath it.
	//
	// So the bike's own occlusion is not rasterised at all. It is an ellipsoid the size
	// of rider-plus-bike, tested analytically against the ray from this fragment toward
	// the sun. It costs about fifteen instructions, it is exact at any distance, it never
	// shimmers because there is no texel grid to snap to, and it tracks the bike with
	// zero latency because it *is* the bike's transform rather than a picture of it.
	//
	// The ellipsoid is oriented to the bike's heading — long fore-aft, narrow across —
	// so the shadow on the ground is a bike-shaped smear that turns when the bike turns,
	// not a drop-shadow blob.
	if ( uGripAerial[ 12 ].w > 0.0 ) {
		vec3 gToB = uGripAerial[ 12 ].xyz - wp;
		float gT = dot( gToB, uGripAerial[ 2 ].xyz );
		// Only fragments the bike is actually between the sun and, and only outside the
		// bike's own body: gT below the ellipsoid's own reach is the bike shading itself,
		// which its normals already do better.
		float gGate = smoothstep( 0.75, 1.35, gT );
		if ( gGate > 0.001 ) {
			vec3 gPerp = gToB - uGripAerial[ 2 ].xyz * gT;
			float gA = gPerp.x * uGripAerial[ 13 ].x + gPerp.z * uGripAerial[ 13 ].y;
			float gB = uGripAerial[ 13 ].x * gPerp.z - uGripAerial[ 13 ].y * gPerp.x;
			float gR = length( vec3( gA / uGripAerial[ 13 ].z, gPerp.y * 0.95, gB / uGripAerial[ 13 ].w ) );
			// Penumbra grows with distance from the caster, the way a real one does: hard
			// under the tyre, soft and gone by the time the bike is a few metres up.
			float gPen = 0.17 + 0.085 * gT;
			float gOcc = 1.0 - smoothstep( 1.0 - gPen, 1.0 + gPen, gR );
			// And it fades out with height off the ground so a bike in the air does not
			// drag a hard black stamp across the landing.
			gLit *= 1.0 - uGripAerial[ 12 ].w * gGate * gOcc * ( 1.0 - smoothstep( 3.0, 11.0, gT ) );
		}
	}

	return vec2( gLit, mix( 1.0, gt.g, uGripAerial[ 8 ].x ) );
}
`;

  C.fog_pars_vertex = /* glsl */ `
#ifdef USE_FOG
	varying float vFogDepth;
#endif
`;

  // mvPosition is in scope from <project_vertex> in every shader that includes this.
  // viewMatrix is orthonormal, so ( v * viewMatrix ) is inverse-rotation of v: that
  // recovers a world-space offset from a view-space one for skinned, morphed and
  // instanced geometry alike, which modelMatrix alone would not.
  //
  // vGripWP is written unconditionally — the terrain shadow needs it whether or not the
  // material took fog.
  C.fog_vertex = /* glsl */ `
	vGripWP = cameraPosition + ( vec4( mvPosition.xyz, 0.0 ) * viewMatrix ).xyz;
#ifdef USE_FOG
	vFogDepth = - mvPosition.z;
#endif
`;

  C.fog_pars_fragment = /* glsl */ `
#ifdef USE_FOG

	uniform vec3 fogColor;
	varying float vFogDepth;

	#ifdef FOG_EXP2
		uniform float fogDensity;
	#else
		uniform float fogNear;
		uniform float fogFar;
	#endif

	// Exact integral of exp( -( y - refY ) / H ) along a straight segment, in metres.
	// Density is capped at e^2 so a ray that dives far below the layer reference cannot
	// produce a numerically silly optical depth.
	float gripLayer( float y0, float y1, float L, float refY, float invH ) {
		float u0 = max( ( y0 - refY ) * invH, -2.0 );
		float u1 = max( ( y1 - refY ) * invH, -2.0 );
		float d = u1 - u0;
		float e0 = exp( - u0 );
		float r = ( abs( d ) < 1e-3 ) ? e0 : ( e0 - exp( - u1 ) ) / d;
		return L * r;
	}

#endif
`;

  C.fog_fragment = /* glsl */ `
#ifdef USE_FOG
{
	if ( uGripAerial[ 0 ].w > 0.5 ) {

		vec3 gRay = vGripWP - cameraPosition;
		float gL = max( length( gRay ), 1e-3 );
		vec3 gDir = gRay / gL;

		float gHaze = gripLayer( cameraPosition.y, vGripWP.y, gL, 0.0, uGripAerial[ 3 ].w );
		float gMist = gripLayer( cameraPosition.y, vGripWP.y, gL, uGripAerial[ 4 ].w, uGripAerial[ 5 ].w );

		vec3 gTau = ( uGripAerial[ 0 ].xyz * gHaze + uGripAerial[ 1 ].xyz * gMist ) * uGripAerial[ 1 ].w;
		// Achromatic ramp past the edge of the drawable world so the last terrain ring
		// dissolves into the horizon instead of ending in a line.
		gTau += vec3( max( gL - uGripAerial[ 6 ].y, 0.0 ) * uGripAerial[ 6 ].z );
		vec3 gT = exp( - min( gTau, vec3( 24.0 ) ) );

		vec3 gSun = uGripAerial[ 2 ].xyz;
		float gCos = clamp( dot( gDir, gSun ), -1.0, 1.0 );
		float gg = uGripAerial[ 2 ].w;
		float gg2 = gg * gg;
		// Clamped for the same reason the dome clamps its Mie lobe: an unbounded forward
		// peak turns every distant ridge on the sun's bearing into a white silhouette.
		float gPM = min( ( 1.0 - gg2 ) / pow( max( 1.0 + gg2 - 2.0 * gg * gCos, 1e-4 ), 1.5 ), 8.0 );
		// Rayleigh phase, flattened toward 1 so the terrain silhouette does not step
		// against the dome where the two models meet.
		float gPR = mix( 1.0, 0.75 * ( 1.0 + gCos * gCos ), 0.55 );

		vec3 gSky = mix( uGripAerial[ 3 ].xyz, uGripAerial[ 4 ].xyz,
			clamp( gDir.y * 1.9 + 0.10, 0.0, 1.0 ) );
		// Below the horizon the dome is not sky, it is hazed distant ground, and the
		// haze in front of a ridge has to converge to the same thing the dome behind it
		// converges to. Same ramp as the dome uses, or the horizon line grows a step
		// exactly where the eye is looking for one.
		gSky = mix( gSky, uGripAerial[ 9 ].xyz, smoothstep( 0.030, -0.170, gDir.y ) );
		vec3 gScatter = gSky * gPR + uGripAerial[ 5 ].xyz * ( gPM * uGripAerial[ 6 ].x );

		gl_FragColor.rgb = gl_FragColor.rgb * gT + gScatter * ( 1.0 - gT );

	} else {

		#ifdef FOG_EXP2
			float gFogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
		#else
			float gFogFactor = smoothstep( fogNear, fogFar, vFogDepth );
		#endif
		gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, gFogFactor );

	}

	// Authoring view: paint the raw sun-shadow term instead of the shaded surface. This
	// is the only place in a built-in shader where gl_FragColor and the shadow varyings
	// are both in scope, which is why the debug lives in the fog chunk.
	#if defined( USE_SHADOWMAP ) && defined( RE_Direct ) && NUM_DIR_LIGHT_SHADOWS > 0
		if ( uGripAerial[ 8 ].z > 0.5 ) {
			float gDbg = getShadow( directionalShadowMap[ 0 ], directionalLightShadows[ 0 ].shadowMapSize,
				directionalLightShadows[ 0 ].shadowIntensity, directionalLightShadows[ 0 ].shadowBias,
				directionalLightShadows[ 0 ].shadowRadius, vDirectionalShadowCoord[ 0 ] );
			// R = shadow map (objects), G = analytic terrain field, B = sky visibility.
			gl_FragColor.rgb = vec3( gDbg, gGripTerrain.x, gGripTerrain.y );
		}
	#endif
}
#endif
`;

  // --- terrain self-shadow into the direct sun -------------------------------
  //
  // The mountain does not cast into the shadow map (the clipmap is nine rings of
  // dynamically rebuilt geometry; putting it through a depth pass per cascade is not
  // affordable, and one cascade wide enough for a 2 km ridge would have metre-scale
  // texels). So the sun's own term is multiplied here by the analytic occlusion,
  // globally, for every lit material — terrain, trees, rocks, rider. That is what puts
  // a ridge shadow *on* the trees standing in the valley below it, not just on the dirt.
  //
  // Only light 0 — the sun added by the Sky constructor before any system runs. A fill
  // light some other module adds later must not be occluded by the mountain.
  //
  // Indirect diffuse is scaled by sky visibility on the same sample, so a gully sees
  // less of the sky dome than the arête above it. Applied at the end of the chunk,
  // after the ambient/probe/hemisphere sum and before lightmaps and IBL.
  const LFB = C.lights_fragment_begin;
  C.lights_fragment_begin = `${LFB
    .replace(
      'IncidentLight directLight;',
      'vec2 gGripTerrain = gripTerrainLight( vGripWP );\nIncidentLight directLight;',
    )
    .replace(
      'getDirectionalLightInfo( directionalLight, directLight );',
      `getDirectionalLightInfo( directionalLight, directLight );
		#if ( UNROLLED_LOOP_INDEX == 0 )
		directLight.color *= gGripTerrain.x;
		#endif`,
    )}
#if defined( RE_IndirectDiffuse )
	irradiance *= gGripTerrain.y;
#endif
`;

  // Publish the parameter block to every built-in shader. cloneUniforms() copies typed
  // arrays by reference, so all materials end up pointing at `aerial`; a texture is
  // copied by reference too, which is why sunOccTexture is allocated once.
  const lib = THREE.ShaderLib as unknown as Record<string, { uniforms: Record<string, THREE.IUniform> }>;
  for (const key of Object.keys(lib)) {
    const entry = lib[key];
    if (!entry || !entry.uniforms) continue;
    if (entry.uniforms.uGripAerial === undefined) entry.uniforms.uGripAerial = { value: aerial };
    if (entry.uniforms.uGripSunOcc === undefined) {
      entry.uniforms.uGripSunOcc = { value: coarseOcc.texture };
    }
    if (entry.uniforms.uGripSunOccFine === undefined) {
      entry.uniforms.uGripSunOccFine = { value: fineOcc.texture };
    }
  }
}

if (typeof window === 'undefined' || !/[?&]noAerial/.test(window.location.search)) {
  installAerialPerspective();
}

/** Authoring/diagnostic hook. Lets the capture harness poke the atmosphere directly. */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__gripSky = {
    aerial,
    params: skyParams,
    sky: activeSky,
    debug: skyDebug,
    shadowStats: () => activeSky.current?.shadowField.stats() ?? null,
    shadowStatsFine: () => activeSky.current?.fineFieldStats() ?? null,
  };
}

// ---------------------------------------------------------------------------
// The scattering model, shared between GLSL and TypeScript.
// ---------------------------------------------------------------------------

const f = (v: number): string => (Number.isInteger(v) ? v.toFixed(1) : String(v));

const BR = SkyTuning.betaR;
const BO = SkyTuning.betaO3;

const skyVert = /* glsl */ `
varying vec3 vWorldDir;
void main() {
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vWorldDir = wp.xyz - cameraPosition;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  #ifdef SKY_FAR_PLANE
    gl_Position.z = gl_Position.w * 0.999999;
  #endif
}
`;

const skyFrag = /* glsl */ `
precision highp float;

varying vec3 vWorldDir;

uniform vec3 uSunDir;
uniform vec4 uAtmos;        // x turbidity, y night, z cloudAltAboveCam, w unused
uniform vec4 uCloud;        // x cover, y erode, z windX, w windZ
uniform vec3 uCloudLit;
uniform vec3 uCloudShade;
uniform vec3 uSunDisc;
uniform vec3 uGroundCol;
uniform vec3 uHorizonCol;
uniform vec2 uShoulder;     // x = knee start, y = headroom above it

const vec3 BETA_R = vec3( ${f(BR[0])}, ${f(BR[1])}, ${f(BR[2])} );
const vec3 BETA_O3 = vec3( ${f(BO[0])}, ${f(BO[1])}, ${f(BO[2])} );
const float O3_CAP = ${f(SkyTuning.ozoneAirMassCap)};
const float BETA_M = ${f(SkyTuning.betaM)};
const float MIE_G = ${f(SkyTuning.mieG)};
const float SUN_RADIANCE = ${f(SkyTuning.sunRadiance)};
const float CLOUD_SCALE = ${f(SkyTuning.cloudScale)};

// Kasten-Young relative air mass. The reason the horizon is 40x the zenith path and
// therefore washes out rather than staying blue.
float airMass( float c ) {
  c = clamp( c, 0.0, 1.0 );
  float z = degrees( acos( c ) );
  return 1.0 / ( c + 0.50572 * pow( max( 96.07995 - z, 0.02 ), -1.6364 ) );
}

vec3 atmosphere( vec3 dir, vec3 sun, float turb ) {
  float mV = airMass( dir.y );
  float mS = airMass( sun.y );
  float ct = clamp( dot( dir, sun ), -1.0, 1.0 );

  vec3 tauR = BETA_R * mV;
  float tauM = BETA_M * turb * mV;
  vec3 tau = tauR + vec3( tauM );

  // Sunlight surviving its own path to the scattering column. This single term is
  // what turns the sky orange at a low sun; no colour ramp is authored anywhere.
  // The ozone term is what stops it turning olive instead — see SkyTuning.betaO3.
  vec3 sTau = BETA_R * mS + vec3( BETA_M * turb * mS * 0.9 ) + BETA_O3 * min( mS, O3_CAP );
  vec3 sunLow = exp( - sTau );
  vec3 sunHigh = exp( - sTau * 0.42 );
  float w = clamp( 0.6 + 0.4 * ct, 0.0, 1.0 ) * ( 1.0 - clamp( dir.y * 1.5, 0.0, 1.0 ) );
  vec3 sunT = mix( sunHigh, sunLow, w );

  float pR = 0.0596831 * ( 1.0 + ct * ct );
  float g2 = MIE_G * MIE_G;
  float pM = ( 1.0 - g2 ) / ( 12.5663706 * pow( max( 1.0 + g2 - 2.0 * MIE_G * ct, 1e-4 ), 1.5 ) );
  // The Henyey-Greenstein peak is ~30x isotropic. Multiplied by a 40-air-mass horizon
  // path it produces a halo an order of magnitude past white, which is exactly the
  // blown-out sky this module exists to fix. Cap it: keep the glare, lose the clip.
  pM = min( pM, ${f(SkyTuning.miePhaseCap)} );

  vec3 num = tauR * pR + vec3( tauM * pM );
  vec3 scat = num / max( tau, vec3( 1e-5 ) ) * ( 1.0 - exp( - tau ) );
  return scat * sunT * SUN_RADIANCE;
}

float h21( vec2 p ) {
  p = fract( p * vec2( 127.1, 311.7 ) );
  p += dot( p, p + 34.23 );
  return fract( p.x * p.y );
}

float vnoise( vec2 p ) {
  vec2 i = floor( p );
  vec2 fr = fract( p );
  fr = fr * fr * ( 3.0 - 2.0 * fr );
  float a = h21( i );
  float b = h21( i + vec2( 1.0, 0.0 ) );
  float c = h21( i + vec2( 0.0, 1.0 ) );
  float d = h21( i + vec2( 1.0, 1.0 ) );
  return mix( mix( a, b, fr.x ), mix( c, d, fr.x ), fr.y );
}

float fbm3( vec2 p ) {
  float s = 0.5714 * vnoise( p );
  p = p * 2.07 + 11.3;
  s += 0.2857 * vnoise( p );
  p = p * 2.03 + 7.1;
  s += 0.1429 * vnoise( p );
  return s;
}

float fbm4( vec2 p ) {
  float s = 0.5333 * vnoise( p );
  p = p * 2.07 + 11.3;
  s += 0.2667 * vnoise( p );
  p = p * 2.03 + 7.1;
  s += 0.1333 * vnoise( p );
  p = p * 2.11 + 3.7;
  s += 0.0667 * vnoise( p );
  return s;
}

// A flat deck sampled where the view ray crosses it. Not a simulation — but it has a
// lit side and a shadowed side, eroded edges and a silver lining, which is what makes
// a cloud read as a solid rather than as a gradient.
vec4 clouds( vec3 dir, vec3 sun ) {
  float cover = uCloud.x;
  if ( cover < 0.02 || dir.y < -0.03 ) return vec4( 0.0 );

  float t = uAtmos.z / max( dir.y, 0.012 );
  vec2 p = ( dir.xz * t + uCloud.zw ) * CLOUD_SCALE;

  // Distance to the deck crossing controls how much detail survives: near the horizon
  // the projection compresses kilometres into a pixel, so fade toward a flat average.
  float far = clamp( ( t - 7000.0 ) / 22000.0, 0.0, 1.0 );

  // Far enough along the deck that a noise cell is thinner than a pixel: skip the
  // whole fbm chain and hand back flat haze. Saves the most expensive pixels on screen
  // and removes the shimmer that sampling at that rate would otherwise produce.
  if ( far > 0.99 ) {
    return vec4( uHorizonCol, cover * 0.8 * smoothstep( -0.03, mix( 0.075, 0.008, cover ), dir.y ) );
  }

  // Two independent warp fields, not one scalar pushed along a fixed axis: a scalar
  // warp shears the field and the result reads as cirrus streaks rather than billows.
  vec2 q = vec2( fbm3( p * 0.55 ), fbm3( p * 0.55 + vec2( 5.7, 2.9 ) ) );
  vec2 pw = p + ( q - 0.5 ) * 1.05;

  float n = fbm4( pw );
  // Threshold mapped so mid coverage lands near the middle of the fbm distribution;
  // 1 - cover sits far out on the tail and only ever carves thin slivers.
  float thr = mix( 0.68, 0.27, cover );
  float soft = 0.20;
  float d = smoothstep( thr, thr + soft, n );

  float e = fbm3( pw * 3.2 + 19.0 );
  d = clamp( d - ( 1.0 - d ) * e * uCloud.y, 0.0, 1.0 );
  d = mix( d, cover * 0.8, far );
  // An overcast has to actually close over. Thresholded noise always leaves holes, so
  // past 0.8 coverage the deck is forced solid and the noise only shades it.
  d = mix( d, 1.0, smoothstep( 0.78, 0.99, cover ) );

  if ( d <= 0.002 ) return vec4( 0.0 );

  // Fake self-shadowing: density sampled a step toward the sun, at two lengths. Two
  // taps give a lit crown and a genuinely dark base rather than a uniform blob.
  vec2 sxz = sun.xz;
  float sl = max( length( sxz ), 1e-4 );
  vec2 so = ( sxz / sl ) * ( 0.20 + 0.30 * ( 1.0 - clamp( sun.y, 0.0, 1.0 ) ) );
  float ds = smoothstep( thr, thr + soft, fbm3( pw + so ) );
  float ds2 = smoothstep( thr, thr + soft, fbm3( pw + so * 2.7 ) );

  float shade = 0.60 * ds + 0.40 * ds2;
  float lit = exp( -1.75 * shade );
  vec3 col = mix( uCloudShade, uCloudLit, lit );

  float ct = clamp( dot( dir, sun ), 0.0, 1.0 );
  col += uCloudLit * pow( ct, 16.0 ) * 0.8 * ( 1.0 - ds );

  // Sit the deck back into the haze as it recedes.
  col = mix( col, uHorizonCol, far );

  // A solid deck has to run all the way down to the horizon; a fade that stops short
  // draws a hard grey line across the sky exactly where a deck should be thickest.
  float a = d * smoothstep( -0.03, mix( 0.075, 0.008, cover ), dir.y );
  return vec4( col, a );
}

float stars( vec3 dir ) {
  vec3 p = dir * 240.0;
  vec3 i = floor( p );
  vec3 fr = fract( p ) - 0.5;
  float h = fract( sin( dot( i, vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 );
  float m = step( 0.9968, h );
  return m * smoothstep( 0.40, 0.0, length( fr ) );
}

void main() {
  vec3 dir = normalize( vWorldDir );
  vec3 sun = normalize( uSunDir );

  // uAtmos.w dims the clear-sky layer under a deck: the strip of sky visible below an
  // overcast should not still be a brilliant summer horizon.
  vec3 col = atmosphere( dir, sun, uAtmos.x ) * uAtmos.w;

  // Night: a residual blue and a star field, so a low sun does not fall off a cliff.
  float night = uAtmos.y;
  if ( night > 0.004 ) {
    vec3 nc = vec3( 0.0060, 0.0098, 0.0225 ) * ( 0.45 + 0.55 * clamp( dir.y * 2.0, 0.0, 1.0 ) );
    col += nc * night;
    if ( dir.y > 0.0 ) col += vec3( 0.85, 0.90, 1.0 ) * stars( dir ) * night * 0.55;
  }

  // Soft shoulder on the sky itself. The forward-scattering lobe around a low sun runs
  // an order of magnitude past white over a large solid angle, and a tone mapper turns
  // that into the flat white field this module exists to get rid of. Compressing here
  // rather than lowering the whole sky keeps the zenith blue and the gradient intact.
  // (three's ACES multiplies by exposure/0.6, so a linear 2.6 here is already ~0.96
  // sRGB out — the knee has to sit low to leave any headroom at all.)
  vec3 over = max( col - uShoulder.x, vec3( 0.0 ) );
  col = min( col, vec3( uShoulder.x ) ) + over / ( 1.0 + over / uShoulder.y );

  // Solar disc, drawn after the shoulder: the sun itself is meant to blow out.
  float sunCos = dot( dir, sun );
  float disc = smoothstep( 0.99966, 0.99989, sunCos );
  col += uSunDisc * disc;

  vec4 cl = clouds( dir, sun );
  col = mix( col, cl.rgb, cl.a );

  // Below the horizon the dome becomes hazed ground so the edge of the terrain rings
  // does not read as the end of the world. The ramp is deliberately wide — a tight one
  // draws a hard line across the frame exactly where the eye is looking for a horizon.
  float below = smoothstep( 0.030, -0.170, dir.y );
  col = mix( col, uGroundCol, below );

  gl_FragColor = vec4( max( col, vec3( 0.0 ) ), 1.0 );

  // A ShaderMaterial does NOT get these for free the way a built-in material does.
  // Without them the dome writes raw linear straight to an 8-bit buffer: it clips at
  // 1.0, ignores exposure, and sits in a different colour space from the terrain it is
  // supposed to be continuous with. This was the actual cause of the blown-out sky.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

// --- CPU mirror of the same model ------------------------------------------------

const DEG = 180 / Math.PI;

function airMassCpu(c: number): number {
  const cc = clamp(c, 0, 1);
  const z = Math.acos(cc) * DEG;
  return 1 / (cc + 0.50572 * Math.pow(Math.max(96.07995 - z, 0.02), -1.6364));
}

/** Scratch for the CPU model; module scope so nothing allocates per frame. */
const rgb = [0, 0, 0];
const sunTrans = [0, 0, 0];

/** Atmospheric transmittance along the sun's own path. Also the sun's colour. */
function sunTransmittance(sunY: number, turb: number, out: number[]): void {
  const mS = airMassCpu(sunY);
  const mO = Math.min(mS, SkyTuning.ozoneAirMassCap);
  for (let i = 0; i < 3; i++) {
    out[i] = Math.exp(-(BR[i] * mS + SkyTuning.betaM * turb * mS * 0.9 + BO[i] * mO));
  }
}

/** Sky radiance in direction (dx,dy,dz). Mirrors atmosphere() in the fragment shader. */
function skyRadiance(
  dx: number, dy: number, dz: number,
  sx: number, sy: number, sz: number,
  turb: number, night: number, out: number[],
): void {
  const mV = airMassCpu(dy);
  const mS = airMassCpu(sy);
  const ct = clamp(dx * sx + dy * sy + dz * sz, -1, 1);

  const tauM = SkyTuning.betaM * turb * mV;
  const pR = 0.0596831 * (1 + ct * ct);
  const g2 = SkyTuning.mieG * SkyTuning.mieG;
  const pM = Math.min(
    (1 - g2) / (12.5663706 * Math.pow(Math.max(1 + g2 - 2 * SkyTuning.mieG * ct, 1e-4), 1.5)),
    SkyTuning.miePhaseCap,
  );

  const w = clamp01(0.6 + 0.4 * ct) * (1 - clamp01(dy * 1.5));
  const mO = Math.min(mS, SkyTuning.ozoneAirMassCap);

  for (let i = 0; i < 3; i++) {
    const tauR = BR[i] * mV;
    const tau = tauR + tauM;
    const sTau = BR[i] * mS + SkyTuning.betaM * turb * mS * 0.9 + BO[i] * mO;
    const sunT = lerp(Math.exp(-sTau * 0.42), Math.exp(-sTau), w);
    const num = tauR * pR + tauM * pM;
    out[i] = (num / Math.max(tau, 1e-5)) * (1 - Math.exp(-tau)) * sunT * SkyTuning.sunRadiance;
  }

  if (night > 0.004) {
    const up = 0.45 + 0.55 * clamp01(dy * 2);
    out[0] += 0.006 * up * night;
    out[1] += 0.0098 * up * night;
    out[2] += 0.0225 * up * night;
  }
}

// --- Light probe sampling directions ---------------------------------------------

const SH_SAMPLES = 96;
const shDirs = new Float32Array(SH_SAMPLES * 3);
{
  // Fibonacci sphere: uniform, no clustering at the poles, deterministic.
  const ga = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < SH_SAMPLES; i++) {
    const y = 1 - (i / (SH_SAMPLES - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const th = ga * i;
    shDirs[i * 3] = Math.cos(th) * r;
    shDirs[i * 3 + 1] = y;
    shDirs[i * 3 + 2] = Math.sin(th) * r;
  }
}

// ---------------------------------------------------------------------------
// Terrain self-shadowing at ridge scale.
//
// The mountain casts no shadows through the shadow map — the clipmap does not render
// into the depth pass, and a cascade wide enough to hold a 2 km ridge would have
// metre-scale texels. So the shadow structure is computed directly from the analytic
// heightfield instead, which is cheaper *and* has unlimited range.
//
// What is stored is the **altitude of the shadow line** S(x,z): the height at which a
// vertical line at this ground column crosses out of the mountain's shadow. Along a ray
// stepped toward the sun,
//
//     S(p) = max( h(p'), S(p') ) - k * step        k = tan( sun elevation )
//
// which falls straight out of the definition S(p) = max over everything ahead of
// ( h - k * distance ). One pass down each line in the sun's direction computes the
// whole grid in O(N^2) — no ray marching per texel. A texel is in shadow iff its
// surface is below S.
//
// Storing S rather than a shadow mask is what lets a 16 m grid look sharp: the shader
// compares against the *pixel's own* world height, so the terminator follows real
// geometry. S itself is a nearly linear ramp away from the occluding crest, which is
// exactly the thing bilinear filtering reproduces well.
//
// The same height grid yields a cheap sky-visibility term (height relative to a 224 m
// local mean), so gullies see less of the sky dome than the arête above them and the
// probe ambient stops being uniform.
//
// Everything is amortised: the grid follows the rider toroidally, only newly exposed
// texels are sampled, and the sweep is sliced across frames.
// ---------------------------------------------------------------------------

const toHalf = THREE.DataUtils.toHalfFloat;

/** A rectangle of absolute texel indices still to be sampled from the heightfield. */
interface FillJob { x0: number; x1: number; z0: number; z1: number; x: number; z: number; }

/** Blur radius, texels, behind the sky-visibility term. 14 * 16 m = 224 m. */
const VIS_RADIUS = 14;
/** Height relative to the local mean, m, over which sky visibility ramps. */
const VIS_LOW = -55;
const VIS_HIGH = 35;
/** Sky visibility floor — the bottom of a gully still sees a strip of sky. */
const VIS_FLOOR = 0.55;

class SunShadowField {
  private terrain: Terrain | null = null;

  private readonly n: number;
  private readonly mask: number;
  private readonly cell: number;
  readonly extent: number;
  private readonly data: Uint16Array;
  private readonly texture: THREE.DataTexture;
  private readonly withVis: boolean;

  /** Terrain height, physical (toroidal) layout. */
  private readonly h: Float32Array;
  /** Shadow-line altitude, physical layout. */
  private readonly s: Float32Array;
  // Sky visibility starts fully open and the shadow line starts below the world, so a
  // half-built field can only ever be too *bright*, never a black mountain.
  /** Sky visibility 0..1, physical layout. */
  private readonly vis: Float32Array;
  /** Scratch for the separable blur behind sky visibility. */
  private readonly blur: Float32Array;

  /** Absolute texel index of logical index 0, and the physical offsets it implies. */
  private baseX = 0;
  private baseZ = 0;
  private haveWindow = false;
  private readonly colA: Int32Array;
  private readonly rowB: Int32Array;

  private readonly jobs: FillJob[] = [];
  private firstFill = true;

  /** 0 = up to date, 1 = horizontal blur pass, 2 = vertical pass + convert. */
  private visStage = 0;
  private visCursor = 0;
  private visBaseX = 0;
  private visBaseZ = 0;

  private needSweep = false;
  private sweeping = false;
  private sweepCursor = 0;
  private sweepMajorX = true;
  private sweepDir = 1;
  private sweepOffI = 0;
  private sweepFrac = 0;
  private sweepKd = 0;
  private sweepLit = false;
  private sunKeyX = Number.POSITIVE_INFINITY;
  private sunKeyY = Number.POSITIVE_INFINITY;
  private sunKeyZ = Number.POSITIVE_INFINITY;

  /** Per-frame work budgets, sized so neither tier ever owns a frame. */
  private readonly fillBudget: number;
  private readonly firstFillBudget: number;
  private readonly sweepLines: number;

  /** True once one complete sweep has reached the GPU. */
  ready = false;
  /** Penumbra in metres of altitude, published to the shader. */
  soft = 4;

  constructor(
    n: number,
    cell: number,
    tex: { data: Uint16Array; texture: THREE.DataTexture },
    withVis: boolean,
    budgets: { fill: number; firstFill: number; sweepLines: number },
    private readonly softGain: number,
    private readonly softMin: number,
    private readonly softMax: number,
  ) {
    this.n = n;
    this.mask = n - 1;
    this.cell = cell;
    this.extent = n * cell;
    this.data = tex.data;
    this.texture = tex.texture;
    this.withVis = withVis;
    this.h = new Float32Array(n * n);
    this.s = new Float32Array(n * n).fill(SHADOW_LIT);
    this.vis = new Float32Array(n * n).fill(1);
    this.blur = withVis ? new Float32Array(n * n) : new Float32Array(0);
    this.colA = new Int32Array(n);
    this.rowB = new Int32Array(n);
    this.fillBudget = budgets.fill;
    this.firstFillBudget = budgets.firstFill;
    this.sweepLines = budgets.sweepLines;
  }

  attach(terrain: Terrain): void {
    this.terrain = terrain;
  }

  /** Diagnostics for the capture harness: is the field actually casting anything? */
  stats(): Record<string, number> {
    let shadowed = 0;
    let visSum = 0;
    let visMin = 1;
    const n2 = this.n * this.n;
    for (let p = 0; p < n2; p++) {
      if (this.s[p] > this.h[p]) shadowed++;
      const v = this.vis[p];
      visSum += v;
      if (v < visMin) visMin = v;
    }
    return {
      ready: this.ready ? 1 : 0,
      shadowFraction: shadowed / n2,
      visMean: visSum / n2,
      visMin,
      soft: this.soft,
      pendingJobs: this.jobs.length,
      cell: this.cell,
      extent: this.extent,
    };
  }

  update(focusX: number, focusZ: number, sunX: number, sunY: number, sunZ: number): void {
    if (!this.terrain) return;
    const N = this.n;

    const wantX = Math.round(focusX / this.cell) - N / 2;
    const wantZ = Math.round(focusZ / this.cell) - N / 2;

    if (!this.haveWindow) {
      this.haveWindow = true;
      this.baseX = wantX;
      this.baseZ = wantZ;
      this.rebuildOffsets();
      this.push(wantX, wantX + N - 1, wantZ, wantZ + N - 1);
      this.restartDerived(true);
    } else if (wantX !== this.baseX || wantZ !== this.baseZ) {
      const dx = wantX - this.baseX;
      const dz = wantZ - this.baseZ;
      if (Math.abs(dx) >= N || Math.abs(dz) >= N) {
        // Teleport or reset: nothing in the window survives.
        this.jobs.length = 0;
        this.push(wantX, wantX + N - 1, wantZ, wantZ + N - 1);
      } else {
        // Two strips, in absolute indices. They overlap in the corner when the rider
        // moves diagonally; sampling that corner twice is cheaper than not sampling it.
        if (dx > 0) this.push(this.baseX + N, wantX + N - 1, wantZ, wantZ + N - 1);
        else if (dx < 0) this.push(wantX, this.baseX - 1, wantZ, wantZ + N - 1);
        if (dz > 0) this.push(wantX, wantX + N - 1, this.baseZ + N, wantZ + N - 1);
        else if (dz < 0) this.push(wantX, wantX + N - 1, wantZ, this.baseZ - 1);
      }
      this.baseX = wantX;
      this.baseZ = wantZ;
      this.rebuildOffsets();
      const far = N >> 4;
      const moved = Math.abs(wantX - this.visBaseX) >= far || Math.abs(wantZ - this.visBaseZ) >= far;
      this.restartDerived(moved);
    }

    // The sun moving is the other reason S goes stale. The threshold is small enough
    // that a time-lapse stays smooth and large enough that a static scene never sweeps.
    if (
      Math.abs(sunX - this.sunKeyX) > 1.5e-3 ||
      Math.abs(sunY - this.sunKeyY) > 1.5e-3 ||
      Math.abs(sunZ - this.sunKeyZ) > 1.5e-3
    ) {
      this.sunKeyX = sunX;
      this.sunKeyY = sunY;
      this.sunKeyZ = sunZ;
      this.needSweep = true;
      this.sweeping = false;
    }

    // --- budgeted pipeline: sample heights, then sky visibility, then sweep ---------
    if (this.jobs.length > 0) {
      this.runFill(this.firstFill ? this.firstFillBudget : this.fillBudget);
      if (this.jobs.length > 0) return;
      this.firstFill = false;
    }
    if (this.visStage !== 0) {
      this.runVis(this.n >> 2);
      if (this.visStage !== 0) return;
    }
    if (this.needSweep) {
      if (!this.sweeping) this.startSweep(sunX, sunY, sunZ);
      this.runSweep(this.sweepLines);
    }
  }

  private restartDerived(withVis: boolean): void {
    // A blur half-done under the old window offsets would be garbage under the new
    // ones, so an in-flight pass always restarts even if the move was small.
    if (this.withVis && (withVis || this.visStage !== 0)) {
      this.visStage = 1;
      this.visCursor = 0;
      this.visBaseX = this.baseX;
      this.visBaseZ = this.baseZ;
    }
    this.needSweep = true;
    this.sweeping = false;
  }

  private push(x0: number, x1: number, z0: number, z1: number): void {
    if (x1 < x0 || z1 < z0) return;
    this.jobs.push({ x0, x1, z0, z1, x: x0, z: z0 });
  }

  private rebuildOffsets(): void {
    for (let i = 0; i < this.n; i++) {
      this.colA[i] = (this.baseX + i) & this.mask;
      this.rowB[i] = ((this.baseZ + i) & this.mask) * this.n;
    }
  }

  private runFill(budget: number): void {
    const terrain = this.terrain!;
    const h = this.h;
    const cell = this.cell;
    const mask = this.mask;
    const N = this.n;
    let left = budget;
    while (left > 0 && this.jobs.length > 0) {
      const j = this.jobs[0];
      const wz = j.z * cell;
      const row = (j.z & mask) * N;
      const span = Math.min(j.x1 - j.x + 1, left);
      for (let k = 0; k < span; k++) {
        const ax = j.x + k;
        h[row + (ax & mask)] = terrain.height(ax * cell, wz);
      }
      left -= span;
      j.x += span;
      if (j.x > j.x1) {
        j.x = j.x0;
        j.z++;
        if (j.z > j.z1) this.jobs.shift();
      }
    }
  }

  /**
   * Sky visibility: a separable box mean of the height field, then this column's height
   * relative to it. Cheap, stable, and at 224 m radius it separates valley floor from
   * ridge top, which is the scale the eye reads as "how much sky is up there".
   */
  private runVis(rows: number): void {
    const N = this.n;
    const R = VIS_RADIUS;
    const invW = 1 / (2 * R + 1);
    const h = this.h;
    const blur = this.blur;
    const colA = this.colA;
    const rowB = this.rowB;

    if (this.visStage === 1) {
      const end = Math.min(N, this.visCursor + rows);
      for (let j = this.visCursor; j < end; j++) {
        const rb = rowB[j];
        let sum = 0;
        for (let t = -R; t <= R; t++) {
          const c = t < 0 ? 0 : t > N - 1 ? N - 1 : t;
          sum += h[rb + colA[c]];
        }
        for (let i = 0; i < N; i++) {
          blur[rb + colA[i]] = sum * invW;
          const o = i - R;
          const nx = i + R + 1;
          sum += h[rb + colA[nx > N - 1 ? N - 1 : nx]] - h[rb + colA[o < 0 ? 0 : o]];
        }
      }
      this.visCursor = end;
      if (end >= N) { this.visStage = 2; this.visCursor = 0; }
      return;
    }

    const span = 1 / (VIS_HIGH - VIS_LOW);
    const end = Math.min(N, this.visCursor + rows);
    for (let i = this.visCursor; i < end; i++) {
      const ca = colA[i];
      let sum = 0;
      for (let t = -R; t <= R; t++) {
        const c = t < 0 ? 0 : t > N - 1 ? N - 1 : t;
        sum += blur[rowB[c] + ca];
      }
      for (let j = 0; j < N; j++) {
        const p = rowB[j] + ca;
        const rel = h[p] - sum * invW;
        const t = clamp01((rel - VIS_LOW) * span);
        this.vis[p] = VIS_FLOOR + (1 - VIS_FLOOR) * t * t * (3 - 2 * t);
        const o = j - R;
        const nz = j + R + 1;
        sum += blur[rowB[nz > N - 1 ? N - 1 : nz] + ca] - blur[rowB[o < 0 ? 0 : o] + ca];
      }
    }
    this.visCursor = end;
    if (end >= N) { this.visStage = 0; this.visCursor = 0; }
  }

  private startSweep(sunX: number, sunY: number, sunZ: number): void {
    this.sweeping = true;
    this.sweepCursor = 0;

    const al = Math.hypot(sunX, sunZ);
    // Sun below the horizon, or within a few degrees of straight up: no meaningful
    // shadow structure. Flag the whole field lit and let the sun's own intensity gate
    // handle the rest.
    this.sweepLit = sunY <= 0.01 || al < 1e-4;
    if (this.sweepLit) return;

    const ax = sunX / al;
    const az = sunZ / al;
    const k = sunY / al; // rise per horizontal metre toward the sun

    // Penumbra: keep it roughly constant in ground metres as the sun climbs, so a low
    // sun gets a crisp terminator and a high one does not get a razor edge.
    this.soft = clamp(k * this.softGain, this.softMin, this.softMax);

    this.sweepMajorX = Math.abs(ax) >= Math.abs(az);
    const major = this.sweepMajorX ? Math.abs(ax) : Math.abs(az);
    const off = this.sweepMajorX ? az / major : ax / major;
    this.sweepDir = (this.sweepMajorX ? ax : az) >= 0 ? 1 : -1;
    this.sweepOffI = Math.floor(off);
    this.sweepFrac = off - this.sweepOffI;
    this.sweepKd = k * (this.cell / major);
  }

  /**
   * One slice of the shadow-line sweep. Lines are independent of each other and each
   * line depends only on the line one step nearer the sun, so slicing by line is exact
   * — a partial sweep is never uploaded, but it is never wrong either.
   */
  private runSweep(lines: number): void {
    const N = this.n;
    const h = this.h;
    const s = this.s;
    const vis = this.vis;
    const data = this.data;
    const colA = this.colA;
    const rowB = this.rowB;
    const dir = this.sweepDir;
    const offI = this.sweepOffI;
    const fr = this.sweepFrac;
    const kd = this.sweepKd;
    const majorX = this.sweepMajorX;
    const lit = this.sweepLit;

    const end = Math.min(N, this.sweepCursor + lines);
    for (let n = this.sweepCursor; n < end; n++) {
      // March from the far side toward the near side so the neighbour one step nearer
      // the sun is already resolved.
      const m = dir > 0 ? N - 1 - n : n;
      const m2 = m + dir;
      const edge = lit || m2 < 0 || m2 >= N;

      // A is the physical offset of this line, A2 of the line one step toward the sun;
      // B indexes along the line. Which of the two is the x axis is the only difference
      // between the two sweep orientations.
      const A = majorX ? colA[m] : rowB[m];
      const A2 = edge ? 0 : majorX ? colA[m2] : rowB[m2];
      const B = majorX ? rowB : colA;

      for (let i = 0; i < N; i++) {
        const p = A + B[i];
        let v: number;
        if (edge) {
          v = SHADOW_LIT;
        } else {
          let i0 = i + offI;
          let i1 = i0 + 1;
          i0 = i0 < 0 ? 0 : i0 > N - 1 ? N - 1 : i0;
          i1 = i1 < 0 ? 0 : i1 > N - 1 ? N - 1 : i1;
          const p0 = A2 + B[i0];
          const p1 = A2 + B[i1];
          const hn = h[p0] + (h[p1] - h[p0]) * fr;
          const sn = s[p0] + (s[p1] - s[p0]) * fr;
          v = (hn > sn ? hn : sn) - kd;
          if (v < SHADOW_LIT) v = SHADOW_LIT;
        }
        s[p] = v;
        data[p * 2] = toHalf(v);
        data[p * 2 + 1] = toHalf(vis[p]);
      }
    }

    this.sweepCursor = end;
    if (end >= N) {
      this.sweeping = false;
      this.needSweep = false;
      this.ready = true;
      this.texture.needsUpdate = true;
    }
  }
}

// ---------------------------------------------------------------------------

export class Sky {
  readonly mesh: THREE.Mesh;
  readonly sun = new THREE.DirectionalLight(0xffffff, Tuning.render.sunIntensity);
  readonly ambient: THREE.HemisphereLight;
  readonly probe = new THREE.LightProbe();
  readonly sunDir = new THREE.Vector3(0.4, 0.55, 0.73).normalize();

  /** Radiance of the horizon and the zenith this frame — read by the weather system. */
  readonly horizonColor = new THREE.Color();
  readonly zenithColor = new THREE.Color();
  /** Sun elevation, sin of the altitude angle. Negative at night. */
  sunElevation = 0;

  /**
   * Terrain self-shadowing, two tiers. Idle until a terrain is attached.
   * Coarse: 5.1 km at 10 m — ridges, and the sky-visibility term.
   * Fine:   512 m at 2 m — the banks, rollers and lips you ride between.
   */
  readonly shadowField = new SunShadowField(
    COARSE_N, COARSE_CELL, coarseOcc, true,
    // The first fill is 262k heightfield samples; at ~0.3 us each that is 75 ms of CPU
    // that must not land in one frame. 8192/frame is 2.4 ms and completes in 32 frames,
    // inside the load before the first ride.
    { fill: 3072, firstFill: 8192, sweepLines: 64 },
    SkyTuning.terrainShadowSoft, SkyTuning.terrainShadowSoftMin, SkyTuning.terrainShadowSoftMax,
  );
  private readonly shadowFine = new SunShadowField(
    FINE_N, FINE_CELL, fineOcc, false,
    // The near window shifts every 2 m of riding and re-sweeps each time, so its slice
    // is kept small: 64 lines is ~0.3 ms, four frames per sweep, and even at 30 km/h a
    // sweep finishes long before the window moves again.
    { fill: 4096, firstFill: 4096, sweepLines: 64 },
    SkyTuning.terrainShadowSoftFine,
    SkyTuning.terrainShadowSoftFineMin,
    SkyTuning.terrainShadowSoftFineMax,
  );

  private readonly uniforms: Record<string, THREE.IUniform>;
  private readonly shBasis: number[] = new Array(9).fill(0);
  private readonly shAcc = new Float64Array(27);
  private readonly sample = [0, 0, 0];
  private readonly ground = [0, 0, 0];
  private readonly horizon = [0, 0, 0];
  private readonly zenith = [0, 0, 0];
  private readonly sunSide = [0, 0, 0];
  private readonly skyIrradiance = [0, 0, 0];
  private cloudPhaseX = 0;
  private cloudPhaseZ = 0;
  private focusX = 0;
  private focusZ = 0;
  /** Camera bearing, unit, XZ. Set by the weather system; see setViewBearing(). */
  private viewDirX = 0;
  private viewDirZ = 0;
  /** Rider occluder centre and heading. Set by the weather system; see setRider(). */
  private riderX = 0;
  private riderY = -1e6;
  private riderZ = 0;
  private riderYaw = 0;
  /** CPU cost of the two shadow fields this frame, ms. Read by the capture harness. */
  fieldMs = 0;
  fieldMsMax = 0;
  fieldMsAvg = 0;
  // Infinity, not NaN: every comparison against NaN is false, which would silently
  // stop the lighting ever being computed.
  private readonly lightKey = new Float64Array(6).fill(Number.POSITIVE_INFINITY);

  /** Time of day in hours, 0..24. Backed by skyParams so weather can drive it. */
  get timeOfDay(): number { return skyParams.timeOfDay; }
  set timeOfDay(h: number) { skyParams.timeOfDay = h; }

  constructor(scene: THREE.Scene) {
    this.uniforms = {
      uSunDir: { value: this.sunDir },
      uAtmos: { value: new THREE.Vector4(skyParams.turbidity, 0, 1800, 0) },
      uCloud: { value: new THREE.Vector4(skyParams.cloudCover, 0.55, 0, 0) },
      uCloudLit: { value: new THREE.Color(1, 1, 1) },
      uCloudShade: { value: new THREE.Color(0.4, 0.45, 0.55) },
      uSunDisc: { value: new THREE.Color(0, 0, 0) },
      uGroundCol: { value: new THREE.Color(0.2, 0.2, 0.2) },
      uHorizonCol: { value: new THREE.Color(0.5, 0.55, 0.6) },
      uShoulder: { value: new THREE.Vector2(SkyTuning.skyKnee, SkyTuning.skyHeadroom) },
    };

    const geo = new THREE.SphereGeometry(1, 40, 24);

    // Drawn after the opaque queue with depth test on: only actual sky pixels pay for
    // the scattering integral and the cloud fbm, which is most of the shader cost.
    const mat = new THREE.ShaderMaterial({
      vertexShader: skyVert,
      fragmentShader: skyFrag,
      uniforms: this.uniforms,
      defines: { SKY_FAR_PLANE: '' },
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 900;
    scene.add(this.mesh);

    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(Tuning.render.shadowMapSize, Tuning.render.shadowMapSize);
    const d = SkyTuning.shadowRadius;
    const cam = this.sun.shadow.camera;
    cam.left = -d; cam.right = d; cam.top = d; cam.bottom = -d;
    // A depth slab sized to what can actually cast, not to the whole light frustum. The
    // old 1..6d range spread 16-bit-equivalent packed depth over 1.2 km, so the constant
    // bias needed to suppress acne was a quarter of a metre — a bike sits 0.4 m off the
    // ground, so a quarter metre of bias is most of its contact shadow. SHADOW_LIFT above
    // the terrain and SHADOW_DROP below it is all the range a mountain surface plus its
    // trees occupies, and it lets the bias come down by 3x.
    cam.near = SHADOW_DIST - SHADOW_LIFT;
    cam.far = SHADOW_DIST + SHADOW_DROP;
    cam.updateProjectionMatrix();
    // Normal-offset does the heavy lifting; a large constant bias is what makes shadows
    // detach from their caster on a slope.
    this.sun.shadow.bias = -0.00013;
    this.sun.shadow.normalBias = 0.022;
    this.sun.shadow.radius = 1.4;
    this.sun.shadow.autoUpdate = true;
    scene.add(this.sun);
    scene.add(this.sun.target);

    // Insurance floor only — the light probe carries the real sky ambient.
    this.ambient = new THREE.HemisphereLight(0x8fb2d8, 0x4a4034, 0.10);
    scene.add(this.ambient);
    scene.add(this.probe);

    scene.fog = new THREE.FogExp2(0x9fb4c4, 0.00042);

    activeSky.current = this;
  }

  /**
   * Hand the sky the heightfield so it can compute terrain self-shadowing. Sky is built
   * in the composition root, which has no system context; the weather system calls this
   * from its init, where `ctx.terrain` is available. Until it is called the shadow field
   * stays idle and the shader term is disabled — nothing goes dark by accident.
   */
  attachTerrain(terrain: Terrain): void {
    this.shadowField.attach(terrain);
    this.shadowFine.attach(terrain);
  }

  /** Diagnostics for the capture harness. */
  fineFieldStats(): Record<string, number> {
    return this.shadowFine.stats();
  }

  /**
   * Where the player is looking, as a unit XZ bearing. The shadow cascade is pushed this
   * way so its texels land on the trail ahead rather than on the hillside behind. Called
   * by the weather system, which is the only piece of the atmosphere with a camera.
   * One frame stale by construction (systems run after Sky.update) and that is fine —
   * the cascade snaps to texels anyway, so a frame of lag is invisible.
   */
  setViewBearing(x: number, z: number): void {
    const l = Math.hypot(x, z);
    if (l < 1e-4) return;
    // Damped, so a flick of the camera does not slide the whole cascade and re-rasterise
    // every shadow edge in one frame.
    this.viewDirX += (x / l - this.viewDirX) * 0.06;
    this.viewDirZ += (z / l - this.viewDirZ) * 0.06;
  }

  /**
   * Where the rider is, so their own contact shadow can be traced analytically. `y` is
   * the contact patch; the occluder is lifted to the middle of the rider from there.
   * Called by the weather system, which is the only piece of the atmosphere with a
   * SystemContext and therefore the only one that can see BikeState.
   */
  setRider(x: number, y: number, z: number, yaw: number): void {
    this.riderX = x;
    this.riderY = y + SkyTuning.riderShadowLift;
    this.riderZ = z;
    this.riderYaw = yaw;
  }

  /** Advance anything animated. Called by the weather system, which owns dt. */
  advance(dt: number): void {
    this.cloudPhaseX += skyParams.windX * dt;
    this.cloudPhaseZ += skyParams.windZ * dt;
  }

  update(scene: THREE.Scene, focusX: number, focusY: number, focusZ: number): void {
    // --- sun position -----------------------------------------------------------
    const t = ((skyParams.timeOfDay - 6) / 12) * Math.PI;
    this.sunDir.set(Math.cos(t) * 0.82, Math.sin(t), 0.34).normalize();
    this.sunElevation = this.sunDir.y;

    const turb = skyParams.turbidity;
    const night = smoothstep(0.06, -0.12, this.sunDir.y);

    // --- lighting: recompute only when the sky actually moved --------------------
    // 96 sky evaluations plus an SH projection is cheap but not free, and in a static
    // scene none of the inputs move at all.
    const k = this.lightKey;
    if (
      Math.abs(k[0] - this.sunDir.y) > 2e-4 ||
      Math.abs(k[1] - turb) > 2e-3 ||
      Math.abs(k[2] - skyParams.cloudCover) > 2e-3 ||
      Math.abs(k[3] - skyParams.sunDim) > 2e-3 ||
      Math.abs(k[4] - skyParams.ambientBoost) > 2e-3 ||
      Math.abs(k[5] - skyParams.cloudBrightness) > 2e-3
    ) {
      k[0] = this.sunDir.y;
      k[1] = turb;
      k[2] = skyParams.cloudCover;
      k[3] = skyParams.sunDim;
      k[4] = skyParams.ambientBoost;
      k[5] = skyParams.cloudBrightness;
      this.computeLighting(turb, night);
    }

    // --- shadows ----------------------------------------------------------------
    // Two halves of one thing: the map handles objects (trees, rocks, the bike), the
    // analytic field handles the mountain shadowing itself at ridge scale.
    this.updateShadow(focusX, focusY, focusZ);
    const t0 = performance.now();
    this.shadowField.update(focusX, focusZ, this.sunDir.x, this.sunDir.y, this.sunDir.z);
    this.shadowFine.update(focusX, focusZ, this.sunDir.x, this.sunDir.y, this.sunDir.z);
    const fieldMs = performance.now() - t0;
    this.fieldMs = fieldMs;
    this.fieldMsMax = Math.max(this.fieldMsMax, fieldMs);
    this.fieldMsAvg += (fieldMs - this.fieldMsAvg) * 0.05;
    this.focusX = focusX;
    this.focusZ = focusZ;

    // --- dome uniforms ----------------------------------------------------------
    const atmos = this.uniforms.uAtmos.value as THREE.Vector4;
    atmos.set(
      turb,
      night,
      Math.max(SkyTuning.cloudAltitude - focusY, 260),
      lerp(1, 0.62, smoothstep(0.5, 0.95, clamp01(skyParams.cloudCover))),
    );

    const cloud = this.uniforms.uCloud.value as THREE.Vector4;
    cloud.set(
      clamp01(skyParams.cloudCover),
      lerp(0.40, 0.16, clamp01(skyParams.cloudCover)),
      this.cloudPhaseX,
      this.cloudPhaseZ,
    );

    this.mesh.position.set(focusX, focusY, focusZ);
    this.mesh.scale.setScalar(Tuning.world.viewDistance * 0.9);

    // --- aerial perspective parameters -----------------------------------------
    this.updateAerial(scene, focusY);
  }

  // -------------------------------------------------------------------------
  // Lighting: sun colour, SH ambient, and the reference sky colours everything
  // else keys off.
  // -------------------------------------------------------------------------
  private computeLighting(turb: number, night: number): void {
    const s = this.sunDir;
    const sunUp = clamp01(s.y);

    sunTransmittance(s.y, turb, sunTrans);
    const dayGate = smoothstep(-0.055, 0.045, s.y);
    const sunScale = dayGate * clamp01(skyParams.sunDim);

    // Direct sun. Colour *is* the transmittance, which is why low sun goes orange.
    this.sun.color.setRGB(
      sunTrans[0] * sunScale + 0.012 * night,
      sunTrans[1] * sunScale + 0.016 * night,
      sunTrans[2] * sunScale + 0.030 * night,
    );
    this.sun.intensity = Tuning.render.sunIntensity;

    // Reference directions used by the aerial perspective and the cloud shading.
    const sxz = Math.hypot(s.x, s.z) || 1;
    const hx = s.x / sxz;
    const hz = s.z / sxz;
    skyRadiance(hx * 0.9987, 0.05, hz * 0.9987, s.x, s.y, s.z, turb, night, this.sunSide);
    // A horizon sample 90 degrees off the sun: the neutral haze colour.
    skyRadiance(-hz * 0.9992, 0.04, hx * 0.9992, s.x, s.y, s.z, turb, night, this.horizon);
    skyRadiance(0, 1, 0, s.x, s.y, s.z, turb, night, this.zenith);

    // Cloud cover greys and flattens everything: the deck scatters light down but kills
    // the blue and the directional bite.
    const cover = clamp01(skyParams.cloudCover);
    const overcast = smoothstep(0.55, 0.95, cover);
    const grey = (this.horizon[0] + this.horizon[1] + this.horizon[2]) / 3;
    for (let i = 0; i < 3; i++) {
      this.horizon[i] = lerp(this.horizon[i], grey * 0.92, overcast * 0.75);
      this.zenith[i] = lerp(this.zenith[i], grey * 0.62, overcast * 0.8);
      this.sunSide[i] = lerp(this.sunSide[i], grey * 0.95, overcast * 0.7);
    }

    // --- SH9 ambient from the sky ------------------------------------------------
    this.shAcc.fill(0);
    const w = (4 * Math.PI) / SH_SAMPLES;
    // Downwelling sky irradiance on a horizontal surface, accumulated as we go: it is
    // what the ground bounce is a reflection of.
    const gr = this.skyIrradiance;
    gr[0] = 0; gr[1] = 0; gr[2] = 0;

    for (let i = 0; i < SH_SAMPLES; i++) {
      const dy = shDirs[i * 3 + 1];
      if (dy < 0) continue;
      const dx = shDirs[i * 3];
      const dz = shDirs[i * 3 + 2];
      skyRadiance(dx, dy, dz, s.x, s.y, s.z, turb, night, this.sample);
      for (let k = 0; k < 3; k++) {
        this.sample[k] = lerp(this.sample[k], grey * 0.78, overcast * 0.8);
      }
      gr[0] += this.sample[0] * dy * w;
      gr[1] += this.sample[1] * dy * w;
      gr[2] += this.sample[2] * dy * w;
      this.accumulate(dx, dy, dz, this.sample, w);
    }

    // Ground bounce: the sky's own downwelling plus the sun, reflected off the mountain.
    const sunE = Tuning.render.sunIntensity * sunUp * sunScale;
    const sunCol = this.sun.color;
    this.ground[0] = (SkyTuning.groundAlbedo[0] * (gr[0] + sunCol.r * sunE)) / Math.PI;
    this.ground[1] = (SkyTuning.groundAlbedo[1] * (gr[1] + sunCol.g * sunE)) / Math.PI;
    this.ground[2] = (SkyTuning.groundAlbedo[2] * (gr[2] + sunCol.b * sunE)) / Math.PI;

    for (let i = 0; i < SH_SAMPLES; i++) {
      const dy = shDirs[i * 3 + 1];
      if (dy >= 0) continue;
      this.accumulate(shDirs[i * 3], dy, shDirs[i * 3 + 2], this.ground, w);
    }

    const amb = SkyTuning.ambientScale * (1 + skyParams.ambientBoost);
    const co = this.probe.sh.coefficients;
    for (let j = 0; j < 9; j++) {
      co[j].set(this.shAcc[j * 3] * amb, this.shAcc[j * 3 + 1] * amb, this.shAcc[j * 3 + 2] * amb);
    }
    this.probe.intensity = 1;

    // Hemisphere floor, tinted from the same sky so it never fights the probe.
    this.ambient.color.setRGB(this.zenith[0], this.zenith[1], this.zenith[2]);
    this.ambient.groundColor.setRGB(this.ground[0], this.ground[1], this.ground[2]);
    this.ambient.intensity = 0.12;

    // --- colours used by the dome ------------------------------------------------
    const discGate = smoothstep(-0.01, 0.03, s.y) * lerp(1, 0.02, smoothstep(0.35, 0.85, cover));
    (this.uniforms.uSunDisc.value as THREE.Color).setRGB(
      sunTrans[0] * SkyTuning.discRadiance * discGate,
      sunTrans[1] * SkyTuning.discRadiance * discGate,
      sunTrans[2] * SkyTuning.discRadiance * discGate,
    );

    const bright = skyParams.cloudBrightness;
    const litScale = (0.30 + 1.10 * clamp01(s.y * 2.4 + 0.10)) * bright;
    const litGate = clamp01(skyParams.sunDim * 0.55 + 0.45);
    (this.uniforms.uCloudLit.value as THREE.Color).setRGB(
      (sunTrans[0] * litScale + this.zenith[0] * 0.35) * litGate,
      (sunTrans[1] * litScale + this.zenith[1] * 0.35) * litGate,
      (sunTrans[2] * litScale + this.zenith[2] * 0.35) * litGate,
    );
    (this.uniforms.uCloudShade.value as THREE.Color).setRGB(
      (this.zenith[0] * 0.70 + this.horizon[0] * 0.40) * bright,
      (this.zenith[1] * 0.70 + this.horizon[1] * 0.40) * bright,
      (this.zenith[2] * 0.70 + this.horizon[2] * 0.40) * bright,
    );

    (this.uniforms.uHorizonCol.value as THREE.Color).setRGB(
      this.horizon[0], this.horizon[1], this.horizon[2],
    );

    // Below-horizon dome: hazed distant ground, slightly darker and warmer than the sky
    // so the terrain rings end in something that looks like more mountain.
    (this.uniforms.uGroundCol.value as THREE.Color).setRGB(
      lerp(this.horizon[0] * 0.94, this.ground[0], 0.18),
      lerp(this.horizon[1] * 0.91, this.ground[1], 0.18),
      lerp(this.horizon[2] * 0.88, this.ground[2], 0.18),
    );

    // The dome dims its clear-sky layer under a deck (uAtmos.w); the aerial perspective
    // has to follow or the terrain silhouette steps against the sky behind it.
    const skyDim = lerp(1, 0.62, smoothstep(0.5, 0.95, cover));
    for (let i = 0; i < 3; i++) {
      this.horizon[i] *= skyDim;
      this.zenith[i] *= skyDim;
      this.sunSide[i] *= skyDim;
    }
    (this.uniforms.uHorizonCol.value as THREE.Color).multiplyScalar(skyDim);
    (this.uniforms.uGroundCol.value as THREE.Color).multiplyScalar(skyDim);

    this.horizonColor.setRGB(this.horizon[0], this.horizon[1], this.horizon[2]);
    this.zenithColor.setRGB(this.zenith[0], this.zenith[1], this.zenith[2]);
  }

  private accumulate(dx: number, dy: number, dz: number, radiance: number[], w: number): void {
    _v.set(dx, dy, dz);
    THREE.SphericalHarmonics3.getBasisAt(_v, this.shBasis);
    for (let j = 0; j < 9; j++) {
      const b = this.shBasis[j] * w;
      this.shAcc[j * 3] += radiance[0] * b;
      this.shAcc[j * 3 + 1] += radiance[1] * b;
      this.shAcc[j * 3 + 2] += radiance[2] * b;
    }
  }

  // -------------------------------------------------------------------------
  // Shadows. The single biggest source of shimmer in a game like this is the shadow
  // frustum sliding continuously with the rider: every sub-texel move re-rasterises
  // every edge. Snapping the frustum centre to whole shadow-map texels in the light's
  // own basis makes the depth map stationary between snaps, so edges stop crawling.
  // -------------------------------------------------------------------------
  private updateShadow(focusX: number, focusY: number, focusZ: number): void {
    const L = this.sunDir;
    // three's DirectionalLightShadow builds its camera with lookAt() and up = +Y, so
    // the light basis has to be derived exactly the same way or the snap is off-axis.
    let rx = L.z * 1 - 0 * L.y;
    let ry = 0;
    let rz = 0 * L.y - L.x * 1;
    let rl = Math.hypot(rx, ry, rz);
    if (rl < 1e-4) { rx = 1; ry = 0; rz = 0; rl = 1; }
    rx /= rl; ry /= rl; rz /= rl;
    const ux = L.y * rz - L.z * ry;
    const uy = L.z * rx - L.x * rz;
    const uz = L.x * ry - L.y * rx;

    const d = SkyTuning.shadowRadius;
    const texel = (2 * d) / Tuning.render.shadowMapSize;

    // Lead the rider. The chase camera trails the bike and looks down the trail, so a
    // box centred on the bike wastes its uphill half on ground nobody is looking at.
    // viewX/viewZ is the camera's own forward bearing, published by the weather system
    // (which is the only part of the atmosphere that gets a SystemContext).
    const lead = d * SkyTuning.shadowLead;
    const fx = focusX + this.viewDirX * lead;
    const fz = focusZ + this.viewDirZ * lead;

    const pr = fx * rx + focusY * ry + fz * rz;
    const pu = fx * ux + focusY * uy + fz * uz;
    const pl = fx * L.x + focusY * L.y + fz * L.z;

    const sr = Math.round(pr / texel) * texel;
    const su = Math.round(pu / texel) * texel;
    const sl = Math.round(pl / texel) * texel;

    const cx = rx * sr + ux * su + L.x * sl;
    const cy = ry * sr + uy * su + L.y * sl;
    const cz = rz * sr + uz * su + L.z * sl;

    const dist = SHADOW_DIST;
    this.sun.position.set(cx + L.x * dist, cy + L.y * dist, cz + L.z * dist);
    this.sun.target.position.set(cx, cy, cz);
    this.sun.target.updateMatrixWorld();

    // A sun on the horizon stretches shadows to infinity and produces nothing but
    // acne; fade the shadow out with the light rather than fighting it.
    this.sun.castShadow = this.sunDir.y > 0.05;
    this.sun.shadow.intensity = smoothstep(0.05, 0.16, this.sunDir.y);
  }

  // -------------------------------------------------------------------------
  private updateAerial(scene: THREE.Scene, focusY: number): void {
    const haze = clamp(skyParams.hazeAmount, 0, 4);
    const blue = SkyTuning.apBlue;
    const grey = SkyTuning.apGrey;

    // Wavelength-selective extinction: blue is scattered out ~4x more than red, which
    // is exactly why a ridge at 2 km goes blue rather than just pale.
    aerial[0] = blue * (BR[0] / BR[2]) + grey;
    aerial[1] = blue * (BR[1] / BR[2]) + grey;
    aerial[2] = blue + grey;
    aerial[3] = skyDebug.aerial; // enable

    const mist = SkyTuning.mistBlue * clamp(skyParams.mistAmount, 0, 4);
    aerial[4] = mist * 0.86;
    aerial[5] = mist * 0.93;
    aerial[6] = mist;
    aerial[7] = haze;

    aerial[8] = this.sunDir.x;
    aerial[9] = this.sunDir.y;
    aerial[10] = this.sunDir.z;
    aerial[11] = SkyTuning.mieG;

    aerial[12] = this.horizon[0];
    aerial[13] = this.horizon[1];
    aerial[14] = this.horizon[2];
    aerial[15] = 1 / SkyTuning.apScaleHeight;

    aerial[16] = this.zenith[0];
    aerial[17] = this.zenith[1];
    aerial[18] = this.zenith[2];
    aerial[19] = SkyTuning.mistRefY;

    aerial[20] = this.sunSide[0];
    aerial[21] = this.sunSide[1];
    aerial[22] = this.sunSide[2];
    aerial[23] = 1 / SkyTuning.mistScaleHeight;

    aerial[24] = SkyTuning.apMieWeight;
    aerial[25] = SkyTuning.farFadeStart;
    aerial[26] = SkyTuning.farFadeRate * haze;
    aerial[27] = 0;

    // Terrain self-shadow. The world-to-uv map is pure scale plus a half-texel: the
    // field is a periodic sampling of the world, so no origin has to be published and
    // the rider can ride out of it forever without a seam appearing anywhere but at
    // half the period — which is past the far plane.
    const field = this.shadowField;
    aerial[28] = 1 / field.extent;
    aerial[29] = 0.5 / COARSE_N;
    aerial[30] = field.soft;
    // Fade the direct-shadow term out with the sun: on the horizon everything is in
    // its own shadow anyway and the terminator would sweep the whole mountain in a
    // frame. Sky visibility is not gated — it applies under any light, including a
    // solid overcast, which is when ambient is *all* there is.
    aerial[31] =
      SkyTuning.terrainShadowStrength * skyDebug.terrainShadow * smoothstep(0.005, 0.05, this.sunDir.y);

    aerial[32] = SkyTuning.skyVisStrength * skyDebug.skyVis;
    aerial[33] = field.ready ? 1 : 0;
    aerial[34] = skyDebug.showShadowTerm;
    aerial[35] = 0;

    const dome = this.uniforms.uGroundCol.value as THREE.Color;
    aerial[36] = dome.r;
    aerial[37] = dome.g;
    aerial[38] = dome.b;
    aerial[39] = 0;

    const fine = this.shadowFine;
    aerial[40] = 1 / fine.extent;
    aerial[41] = 0.5 / FINE_N;
    aerial[42] = this.focusX;
    aerial[43] = this.focusZ;
    aerial[44] = FINE_BLEND_START;
    aerial[45] = FINE_BLEND_END;
    aerial[46] = fine.soft;
    aerial[47] = fine.ready && field.ready ? 1 : 0;

    // Rider contact shadow. Gated on the sun being up at all — at civil twilight there is
    // no direct light left for it to remove.
    aerial[48] = this.riderX;
    aerial[49] = this.riderY;
    aerial[50] = this.riderZ;
    aerial[51] =
      SkyTuning.riderShadowStrength * skyDebug.rider
      * smoothstep(0.02, 0.09, this.sunDir.y) * clamp01(skyParams.sunDim);
    aerial[52] = Math.cos(this.riderYaw);
    aerial[53] = Math.sin(this.riderYaw);
    aerial[54] = SkyTuning.riderShadowLength;
    aerial[55] = SkyTuning.riderShadowWidth;

    // Keep stock fog roughly in agreement for anything that misses the parameter block.
    const fog = scene.fog as THREE.FogExp2 | null;
    if (fog && (fog as THREE.FogExp2).isFogExp2) {
      fog.density = (0.00030 + 0.00050 * (1 - clamp01(focusY / Tuning.world.reliefHeight))) * haze;
      fog.color.setRGB(this.horizon[0], this.horizon[1], this.horizon[2]);
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    if (activeSky.current === this) activeSky.current = null;
  }
}

const _v = new THREE.Vector3();
